import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import { Badge, Card, CardHeader, EmptyState, ErrorState, Input, LoadingBlock, Table, Td, Th, Tr } from '../components/ui';
import { ContextBar, PeriodControls, ReportPage, useReport, useReportParams } from './FinancialReports';

type Position = {
  currency: string;
  loanPayable: string;
  advanceReceivable: string;
  equityContributed: string;
  distributions: string;
  loanRepaid: string;
  informationalNet: string;
};

type Movements = Record<
  | 'loanReceived'
  | 'loanRepaid'
  | 'expensesPaidAsLoan'
  | 'loanAdjustments'
  | 'advancesPaid'
  | 'advancesReturned'
  | 'advanceAdjustments'
  | 'capitalContributed'
  | 'expensesPaidAsCapital'
  | 'capitalAdjustments'
  | 'distributions',
  string
>;

interface PartnerCurrency {
  currency: string;
  beginning: Position;
  movements: Movements;
  closing: Position;
  loanReconciles: boolean;
  advancesReconcile: boolean;
}

/* -------------------------------------------------------------------------- */
/* Every partner (§9.1)                                                        */
/* -------------------------------------------------------------------------- */

export function PartnersStatementPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const { from, to, search } = useReportParams();
  const { data, isLoading, error, refetch } = useReport<any>('/financial-reports/partners', { from, to });

  return (
    <ReportPage title={t('nav.frPartners')} subtitle={t('fr.partners.subtitle')} controls={<PeriodControls />} exportPath="/financial-reports/partners/export" exportParams={{ from, to }}>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-5">
          <ContextBar context={data.context} />
          <Card padded={false}>
            {data.partners.length === 0 ? (
              <EmptyState title={t('fr.partners.none')} hint="" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('ptx.partner')}</Th>
                    <Th>{t('fr.partners.share')}</Th>
                    <Th>{t('fr.currency')}</Th>
                    <Th align="end">{t('fr.partners.loan')}</Th>
                    <Th align="end">{t('fr.partners.advances')}</Th>
                    <Th align="end">{t('fr.partners.capital')}</Th>
                    <Th align="end">{t('fr.partners.distributions')}</Th>
                    <Th align="end">{t('fr.partners.net')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.partners.flatMap((p: any) => {
                    const rows: PartnerCurrency[] = p.currencies.length ? p.currencies : [null];
                    return rows.map((c, i) => (
                      <Tr key={`${p.partner.id}-${c?.currency ?? 'none'}`} onClick={() => navigate(`/financial-reports/partners/${p.partner.id}?${search}`)}>
                        <Td>
                          {i === 0 && (
                            <span className="inline-flex items-center gap-2">
                              <span className="font-medium text-[var(--text-primary)]">{localized(p.partner)}</span>
                              {p.partner.status !== 'ACTIVE' && <Badge tone="neutral">{p.partner.status}</Badge>}
                            </span>
                          )}
                        </Td>
                        <Td className="whitespace-nowrap">
                          {i === 0 &&
                            (p.share ? (
                              <span className="tabular">
                                {Number(p.share.percentage).toLocaleString(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { maximumFractionDigits: 6 })}%
                                <span className="ms-1.5 text-caption text-muted">{t('fr.partners.since', { date: formatDate(p.share.effectiveFrom, locale) })}</span>
                              </span>
                            ) : (
                              <span className="text-caption text-muted">{t('fr.partners.noShare')}</span>
                            ))}
                        </Td>
                        {c ? (
                          <>
                            <Td className="font-mono text-mono-sm">{c.currency}</Td>
                            <Td align="end" className="tabular">
                              <Opening value={c.beginning.loanPayable} currency={c.currency} />
                              {formatMoney(c.closing.loanPayable, c.currency, locale)}
                              {!c.loanReconciles && <Badge tone="error" className="ms-1">{t('fr.partners.notReconciled')}</Badge>}
                            </Td>
                            <Td align="end" className="tabular">
                              <Opening value={c.beginning.advanceReceivable} currency={c.currency} />
                              {formatMoney(c.closing.advanceReceivable, c.currency, locale)}
                              {!c.advancesReconcile && <Badge tone="error" className="ms-1">{t('fr.partners.notReconciled')}</Badge>}
                            </Td>
                            <Td align="end" className="tabular">{formatMoney(c.closing.equityContributed, c.currency, locale)}</Td>
                            <Td align="end" className="tabular">{formatMoney(c.closing.distributions, c.currency, locale)}</Td>
                            <Td align="end" className="tabular text-muted">{formatMoney(c.closing.informationalNet, c.currency, locale)}</Td>
                          </>
                        ) : (
                          <Td colSpan={6} className="text-caption text-muted">{t('fr.card.none')}</Td>
                        )}
                      </Tr>
                    ));
                  })}
                </tbody>
              </Table>
            )}
          </Card>
          {data.totals.length > 0 && (
            <Card padded={false}>
              <CardHeader title={t('fr.partners.totals')} subtitle={t('fr.notice.NO_OFFSET')} className="p-5 pb-0" />
              <Table>
                <thead>
                  <tr>
                    <Th>{t('fr.currency')}</Th>
                    <Th align="end">{t('fr.partners.loan')}</Th>
                    <Th align="end">{t('fr.partners.advances')}</Th>
                    <Th align="end">{t('fr.partners.capital')}</Th>
                    <Th align="end">{t('fr.partners.distributions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.totals.map((c: Position) => (
                    <Tr key={c.currency}>
                      <Td className="font-mono text-mono-sm">{c.currency}</Td>
                      <Td align="end" className="tabular">{formatMoney(c.loanPayable, c.currency, locale)}</Td>
                      <Td align="end" className="tabular">{formatMoney(c.advanceReceivable, c.currency, locale)}</Td>
                      <Td align="end" className="tabular">{formatMoney(c.equityContributed, c.currency, locale)}</Td>
                      <Td align="end" className="tabular">{formatMoney(c.distributions, c.currency, locale)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </div>
      )}
    </ReportPage>
  );
}

/** Where a balance started the period, small, above where it ended. */
function Opening({ value, currency }: { value: string; currency: string }) {
  const { t, locale } = useI18n();
  return (
    <span className="block text-caption text-muted" title={t('fr.partners.beginning')}>
      {t('fr.partners.beginning')}: {formatMoney(value, currency, locale)}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* One partner (§9.2)                                                          */
/* -------------------------------------------------------------------------- */

const MOVEMENT_GROUPS: Array<{ title: TranslationKey; begin: keyof Position; end: keyof Position; lines: Array<keyof Movements> }> = [
  { title: 'fr.partners.loan', begin: 'loanPayable', end: 'loanPayable', lines: ['loanReceived', 'loanRepaid', 'expensesPaidAsLoan', 'loanAdjustments'] },
  { title: 'fr.partners.advances', begin: 'advanceReceivable', end: 'advanceReceivable', lines: ['advancesPaid', 'advancesReturned', 'advanceAdjustments'] },
  { title: 'fr.partners.capital', begin: 'equityContributed', end: 'equityContributed', lines: ['capitalContributed', 'expensesPaidAsCapital', 'capitalAdjustments'] },
  { title: 'fr.partners.distributions', begin: 'distributions', end: 'distributions', lines: ['distributions'] },
];

/** A movement that takes the balance down reads with its minus sign. */
const DOWN: Array<keyof Movements> = ['loanRepaid', 'advancesReturned'];

export function PartnerStatementPage() {
  const { id } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const { from, to } = useReportParams();
  const [q, setQ] = useState('');
  const { data, isLoading, error, refetch } = useReport<any>(`/financial-reports/partners/${id}`, { from, to }, !!id);

  const rows = useMemo(() => {
    const all: any[] = data?.rows ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((r) =>
      [r.number, r.reference, r.notes, r.expense?.description, t(`ptx.${r.type}` as TranslationKey), r.account ? localized(r.account) : null, r.amount]
        .filter(Boolean)
        .some((s: string) => String(s).toLowerCase().includes(needle)),
    );
  }, [data, q, t, localized]);

  return (
    <ReportPage
      title={data ? `${t('fr.partners.statement')} — ${localized(data.partner)}` : t('fr.partners.statement')}
      controls={<PeriodControls />}
      exportPath={id ? `/financial-reports/partners/${id}/export` : undefined}
      exportParams={{ from, to }}
    >
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-5">
          <ContextBar context={data.context} />
          <Card>
            <CardHeader
              title={t('fr.partners.shareHistory')}
              action={
                <Link to={`/partners/${data.partner.id}`} className="text-body-sm text-primary-600 hover:underline dark:text-primary-400">
                  {t('nav.partners')}
                </Link>
              }
            />
            {data.shares.length === 0 ? (
              <p className="text-body-sm text-muted">{t('fr.partners.noShare')}</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {data.shares.map((s: any) => (
                  <li key={s.effectiveFrom}>
                    <Badge tone={data.share?.effectiveFrom === s.effectiveFrom ? 'brand' : 'neutral'}>
                      {Number(s.percentage).toLocaleString(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { maximumFractionDigits: 6 })}% · {t('fr.partners.since', { date: formatDate(s.effectiveFrom, locale) })}
                      {s.effectiveTo && ` · ${t('fr.partners.until', { date: formatDate(s.effectiveTo, locale) })}`}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {data.currencies.map((c: PartnerCurrency) => (
            <Card key={c.currency} padded={false}>
              <CardHeader
                title={<span className="font-mono">{c.currency}</span>}
                className="p-5 pb-0"
                action={
                  <Badge tone={c.loanReconciles && c.advancesReconcile ? 'success' : 'error'} dot>
                    {c.loanReconciles && c.advancesReconcile ? t('fr.partners.reconciles') : t('fr.partners.notReconciled')}
                  </Badge>
                }
              />
              <div className="grid gap-px overflow-hidden sm:grid-cols-2 xl:grid-cols-4">
                {MOVEMENT_GROUPS.map((g) => (
                  <div key={g.title} className="p-5">
                    <p className="mb-2 text-overline uppercase text-[var(--text-tertiary)]">{t(g.title)}</p>
                    <dl className="space-y-1 text-body-sm">
                      {g.title !== 'fr.partners.distributions' && (
                        <div className="flex justify-between gap-3">
                          <dt className="text-muted">{t('fr.partners.beginning')}</dt>
                          <dd className="tabular">{formatMoney(c.beginning[g.begin], c.currency, locale)}</dd>
                        </div>
                      )}
                      {g.lines.map((l) => (
                        <div key={l} className="flex justify-between gap-3">
                          <dt className="text-muted">{t(`fr.partners.mv.${l}` as TranslationKey)}</dt>
                          <dd className="tabular">
                            {DOWN.includes(l) && Number(c.movements[l]) !== 0 ? '−' : ''}
                            {formatMoney(c.movements[l], c.currency, locale)}
                          </dd>
                        </div>
                      ))}
                      <div className="flex justify-between gap-3 border-t hairline pt-1 font-medium">
                        <dt>{t('fr.partners.closing')}</dt>
                        <dd className="tabular">{formatMoney(c.closing[g.end], c.currency, locale)}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
              <p className="px-5 pb-4 text-caption text-muted">
                {t('fr.partners.net')}: {formatMoney(c.closing.informationalNet, c.currency, locale)} — {t('fr.notice.NO_OFFSET')}
              </p>
            </Card>
          ))}

          <Card padded={false}>
            <CardHeader
              title={t('fr.partners.statement')}
              className="p-5 pb-0"
              action={<Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('fr.partners.search')} aria-label={t('fr.partners.search')} className="!w-56" />}
            />
            {rows.length === 0 ? (
              <EmptyState title={t('fr.partners.noRows')} hint="" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('ptx.date')}</Th>
                    <Th>{t('ptx.number')}</Th>
                    <Th>{t('ptx.type')}</Th>
                    <Th align="end">{t('ptx.amount')}</Th>
                    <Th>{t('ptx.account')}</Th>
                    <Th align="end">{t('fr.partners.loan')}</Th>
                    <Th align="end">{t('fr.partners.advances')}</Th>
                    <Th align="end">{t('fr.partners.capital')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: any) => (
                    <Tr key={r.id} onClick={() => navigate(`/partner-transactions/${r.id}`)} className={clsx(!r.counted && 'opacity-70')}>
                      <Td className="whitespace-nowrap tabular text-muted">{formatDate(r.date, locale)}</Td>
                      <Td className="font-mono text-mono-sm">{r.number}</Td>
                      <Td>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span>{t(`ptx.${r.type}` as TranslationKey)}</span>
                          {r.reversed && <Badge tone="error">{t('fr.partners.reversed')}</Badge>}
                          {r.beforeCutover && <Badge tone="neutral">{t('fr.partners.history')}</Badge>}
                          {r.noCompanyCash && r.counted && <Badge tone="info">{t('fr.partners.noCash')}</Badge>}
                        </div>
                        {r.expense && <div className="mt-0.5 text-caption text-muted">{t('fr.partners.expensePaid', { description: r.expense.description })}</div>}
                        {r.reference && <div className="mt-0.5 font-mono text-caption text-muted">{r.reference}</div>}
                      </Td>
                      <Td align="end" className="tabular">{formatMoney(r.amount, r.currency, locale)}</Td>
                      <Td className="text-muted">{r.account ? localized(r.account) : '—'}</Td>
                      <Td align="end" className="tabular">{r.balancesAfter ? formatMoney(r.balancesAfter.loanPayable, r.currency, locale) : '—'}</Td>
                      <Td align="end" className="tabular">{r.balancesAfter ? formatMoney(r.balancesAfter.advanceReceivable, r.currency, locale) : '—'}</Td>
                      <Td align="end" className="tabular">{r.balancesAfter ? formatMoney(r.balancesAfter.equityContributed, r.currency, locale) : '—'}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>
      )}
    </ReportPage>
  );
}
